// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Linking, Modal, Platform, Pressable, ScrollView, StyleSheet, TextInput } from 'react-native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { Text, View } from '@/components/Themed';
import { KEYCLOAK_BASE } from '@/auth/oidc';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import appColors from '@/theme/colors';
import { COURSE_ADMIN_COUNTRIES, DEFAULT_COURSE_ADMIN_COUNTRY_CODE } from '@/features/course-admin/countries';
import {
  ALL_COUNTRIES,
  courseListParams,
  filterCourses,
  OSM_FEATURE_KINDS,
  pageOf,
  parseCourseListParams,
  type AnalysisFilter,
  type CourseListFilters,
  // Aliased: OsmFeatureCounts below is what live OSM holds; this is what the import stored.
  type OsmFeatureCounts as StoredOsmFeatureCounts,
} from '@/features/course-admin/courseListView';
import { CourseAnalysisStatusPanel } from '@/features/course-trace/CourseAnalysisStatusPanel';
import { CourseHoleMapModal } from '@/features/course-trace/CourseHoleMapModal';

type OsmFeatureCounts = {
  holes: number;
  greens: number;
  tees: number;
  terrain: number;
};

type OsmCountsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; counts: OsmFeatureCounts }
  | { status: 'error' };

/** OSM's per-course feature counts (holes/greens/tees/terrain) — what live OSM data actually holds for one course. */
function useOsmFeatureCounts(
  apiBaseUrl: string,
  token: string | null | undefined,
  osmType: string | null,
  osmId: number | null,
  enabled: boolean,
): OsmCountsState {
  const [state, setState] = useState<OsmCountsState>({ status: 'idle' });

  useEffect(() => {
    if (!enabled || !token || !osmType || !osmId) {
      setState({ status: 'idle' });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/osm/courses/${osmType}/${osmId}/counts`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        if (!response.ok) {
          setState({ status: 'error' });
          return;
        }
        const body = (await response.json()) as Record<string, unknown>;
        setState({
          status: 'loaded',
          counts: {
            holes: typeof body.holes === 'number' ? body.holes : 0,
            greens: typeof body.greens === 'number' ? body.greens : 0,
            tees: typeof body.tees === 'number' ? body.tees : 0,
            terrain: typeof body.terrain === 'number' ? body.terrain : 0,
          },
        });
      } catch {
        if (!cancelled) {
          setState({ status: 'error' });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, token, osmType, osmId, enabled]);

  return state;
}

function describeProgress(
  progress: ImportProgress,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  switch (progress.phase) {
    case 'searching':
      return t('golfCourseAdminPage.progressSearching');
    case 'found':
      return t('golfCourseAdminPage.progressFound', { count: String(progress.totalFoundInCountry) });
    case 'checkingHoleData':
      return progress.importedSoFar > 0
        ? t('golfCourseAdminPage.progressCheckingHoleDataWithImports', {
            checked: String(progress.checkedCount),
            total: String(progress.total),
            imported: String(progress.importedSoFar),
          })
        : t('golfCourseAdminPage.progressCheckingHoleData', {
            checked: String(progress.checkedCount),
            total: String(progress.total),
          });
    case 'importing':
      return t('golfCourseAdminPage.progressImporting', { count: String(progress.total) });
    default:
      return '';
  }
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

type ImportedCourse = { courseId: string; name: string };

type ImportProgress =
  | { phase: 'searching' }
  | { phase: 'found'; totalFoundInCountry: number }
  | { phase: 'checkingHoleData'; checkedCount: number; total: number; importedSoFar: number }
  | { phase: 'importing'; total: number };

type ImportState =
  | { status: 'idle' }
  | { status: 'importing'; progress: ImportProgress | null }
  | { status: 'done'; importedCount: number; skippedCount: number; failedCount: number; imported: ImportedCourse[] }
  | { status: 'error' };

/** The catalogue-wide OSM feature import: every course with an OSM identity but no OSM terrain
 * rows yet gets its geometry and terrain fetched from OpenStreetMap. Runs for minutes (Overpass
 * is rate-limited, one course a second at best), so progress carries the course being visited. */
type FeatureImportState =
  | { status: 'idle' }
  | { status: 'running'; visited: number; total: number; name: string | null }
  | { status: 'done'; totalVisited: number; refreshedCount: number; failedCount: number; skippedCount: number }
  | { status: 'error' };

/** Running counts for the "Backfill missing analysis" sweep — courses whose analysisState is
 * neverRequested, each auto-matched against OSM if possible and always queued for satellite
 * analysis regardless. */
type BackfillCounts = { matched: number; triggeredWithoutMatch: number; failed: number };

type BackfillState =
  | { status: 'idle' }
  | ({ status: 'running' } & BackfillCounts)
  | ({ status: 'done' } & BackfillCounts)
  | { status: 'error' };

/**
 * The three visibilities an admin sets from here — the same set the course editor models.
 * `pending` is left out on purpose: it means "a user submitted this and it is waiting for
 * approval", which is something that happens to a course rather than something declared about one.
 */
const SETTABLE_VISIBILITIES = ['official', 'private', 'disabled'] as const;

type SettableVisibility = (typeof SETTABLE_VISIBILITIES)[number];

/**
 * The bulk "Set visibility" action. `done` carries both counts because a run can half-succeed —
 * the BFF patches each course on its own and reports which ones took.
 */
type VisibilityActionState =
  | { status: 'idle' }
  | { status: 'running' }
  | { status: 'done'; updated: number; failed: number }
  | { status: 'error' };

/**
 * Mirrors SatelliteAnalysisStatusEndpoints.DeriveState; null means the bulk lookup found nothing
 * for this id (geodata unreachable, or the request predates that lookup).
 *
 * `noGeodata` — no geo_courses row at all — is the one state that says the per-course actions
 * cannot work yet: analyze, flyover render and OSM refresh all resolve the row first and 404
 * without it. `null` is not the same claim: geodata did not answer, so nothing is known and the
 * buttons stay live.
 */
type AnalysisState =
  | 'disabled'
  | 'noGeodata'
  | 'neverRequested'
  | 'pending'
  | 'abandoned'
  | 'analysed'
  | null;

type CourseRow = {
  id: string;
  name: string;
  clubName: string;
  city: string;
  country: string;
  /** Club coordinates — the catalogue has no per-course position. */
  lat: number;
  lon: number;
  visibility: string;
  par: number;
  holesCount: number;
  osmType: string | null;
  osmId: number | null;
  analysisState: AnalysisState;
  /** What the OSM import has stored for this course. All zero when nothing was imported. */
  osmFeatures: StoredOsmFeatureCounts;
  /**
   * The latest flyover job, or null when none was ever queued (or the lookup failed) —
   * so a null reads as "unknown", never as a false "not rendered". Optional so an older BFF
   * that does not send it renders the row without a flyover line at all.
   */
  flyover?: { latestStatus: string; renderedHoles: number; rendered: boolean } | null;
  /**
   * Holes with both a tee and a green — what a render would actually produce. 0 is a course with
   * no hole geometry to fly (nothing imported, nothing analysed), so the render would refuse;
   * null/undefined is "geodata did not say", which must leave the action offered.
   */
  flyableHoles?: number | null;
  /**
   * What Golf i Sverige's published scorecard contributed to this course, or null/undefined when
   * it contributed nothing — or when the BFF could not reach golf-courses-service to ask. Both
   * render as no line at all, never as "0 holes imported", which would claim an import happened
   * and found an empty card.
   */
  scorecardImport?: { importedAtUtc: string; holes: number; tees: number } | null;
};

const NO_OSM_FEATURES: StoredOsmFeatureCounts = { greens: 0, holes: 0, bunkers: 0, fairways: 0 };

/**
 * The import timestamp as a plain local date. Falls back to the raw value rather than rendering
 * "Invalid Date" if the BFF ever sends something unparseable — the point of the line is the
 * counts, and a date nobody can read should not swallow them.
 */
const formatImportDate = (value: string): string => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
};

/** Stable identity, so the filtered/paged memos don't recompute while the list is loading. */
const EMPTY_COURSES: CourseRow[] = [];

type CoursesState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; totalCount: number; courses: CourseRow[] }
  | { status: 'error' };

/**
 * Per-row "Refresh OSM" state and, once done, the delta geodata reported.
 *
 * A failure carries the HTTP status because the reasons are not interchangeable and the admin
 * can only act on one of them: 404 is a course with no geodata row, 409 a course with no OSM
 * identity to refresh from, 502 an Overpass outage nobody here can fix. `null` is a request that
 * never got an answer at all.
 */
type OsmRefreshRowState =
  | { status: 'running' }
  | { status: 'failed'; httpStatus: number | null }
  | { status: 'done'; written: number; updated: number; removed: number; unchanged: number; skipped: number };

/** Per-row "Start analyze" request state, separate from the course's own analysisState (that
 * comes from the last full list fetch; this is the in-flight request against one row). */
type AnalyzeRowState =
  | { status: 'running' }
  | { status: 'queued' }
  /** The BFF's courseAdmin.courses.analyze.* code — it already tells not_found from unavailable. */
  | { status: 'failed'; code?: string };

/** One flyover render job as geodata reports it (see FlyoverJobResponse). */
type FlyoverJob = {
  jobId: string;
  status: 'queued' | 'succeeded' | 'failed';
  requestedAt: string;
  finishedAt: string | null;
  workerId: string | null;
  renderedHoles: number | null;
  skippedHoles: number | null;
  error: string | null;
};

/**
 * flyableHoles is how many holes have both a tee and a green — what a render would actually
 * produce. Optional so an older geodata that does not send it cannot disable the button.
 */
type FlyoverStatus = { workerOnline: boolean; flyableHoles?: number; latestJob: FlyoverJob | null };

type FlyoverRequestState = 'idle' | 'requesting' | 'queued' | 'failed';

/**
 * What to tell the admin when a per-course action fails.
 *
 * These three actions fail for the same handful of reasons, and only some of them are the
 * admin's to fix — so each reason gets its own line rather than one "it failed" that covers a
 * missing geodata row, an Overpass outage and an unconfigured analyser alike.
 */
function osmRefreshFailure(httpStatus: number | null): { key: string; params?: Record<string, string> } {
  switch (httpStatus) {
    case 404:
      return { key: 'golfCourseAdminPage.actionNoGeodata' };
    case 409:
      return { key: 'golfCourseAdminPage.osmRefreshNoIdentity' };
    case 502:
      return { key: 'golfCourseAdminPage.osmRefreshOverpassSilent' };
    case null:
      return { key: 'golfCourseAdminPage.osmRefreshFailed' };
    default:
      return { key: 'golfCourseAdminPage.osmRefreshFailedStatus', params: { status: String(httpStatus) } };
  }
}

function analyzeFailureKey(code: string | undefined): string {
  switch (code) {
    // The request carries the row's position, and geodata creates its own record from that when
    // it has none — so a not_found here is the narrower complaint that no position was sent,
    // not the general "no geodata record" the other two actions report.
    case 'courseAdmin.courses.analyze.not_found':
      return 'golfCourseAdminPage.analyzeNoPosition';
    case 'courseAdmin.courses.analyze.unavailable':
      return 'golfCourseAdminPage.analysisNotConfigured';
    default:
      return 'courseAnalysis.status.runFailed';
  }
}

function flyoverFailureKey(code: string | undefined): string {
  switch (code) {
    case 'courseAdmin.flyovers.render.not_found':
      return 'golfCourseAdminPage.actionNoGeodata';
    case 'courseAdmin.flyovers.render.no_holes':
      return 'golfCourseAdminPage.flyoverNoHoles';
    default:
      return 'golfCourseAdminPage.flyoverRenderFailed';
  }
}

/**
 * The course's location on Esri World Imagery — the same satellite source the analyser reads
 * — so an admin can eyeball whether the catalogue coordinate is actually on the course before
 * spending an analysis on it. Zoom 16 frames a whole 18-hole course.
 *
 * ArcGIS's Map Viewer takes `center` as lon,lat, `level` (only honoured alongside `center`),
 * and the basemap as `basemapUrl` = a full map-service URL. A `basemap=satellite` shorthand is
 * silently ignored and you get the default topographic map — which is what shipped first.
 */
const ESRI_WORLD_IMAGERY = 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer';

/** The course's own OSM object when its identity is known, else the map at its position. */
function osmUrl(course: { osmType: string | null; osmId: number | null; lat?: number | null; lon?: number | null }): string {
  if (course.osmType && course.osmId) {
    return `https://www.openstreetmap.org/${course.osmType}/${course.osmId}`;
  }
  return `https://www.openstreetmap.org/#map=17/${(course.lat as number).toFixed(5)}/${(course.lon as number).toFixed(5)}`;
}

function esriImageryUrl(lat: number, lon: number): string {
  return `https://www.arcgis.com/apps/mapviewer/index.html?center=${lon.toFixed(6)},${lat.toFixed(6)}&level=16&basemapUrl=${encodeURIComponent(ESRI_WORLD_IMAGERY)}`;
}

/**
 * Admin-only tool to bulk-import golf courses from OpenStreetMap for a whole country (see
 * AdminMenu in app/layout/RootLayoutNav.tsx). Imported courses land as CourseVisibility.Disabled,
 * pending manual review — this screen's result list is the only record of what a run imported.
 */
export default function GolfCourseAdminScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const { isAdmin } = useEffectiveRoles(user);
  const router = useRouter();
  const { q, country, status, osm, page } = useLocalSearchParams<{
    q?: string;
    country?: string;
    status?: string;
    osm?: string;
    page?: string;
  }>();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const requestIdRef = useRef<string | null>(null);
  const coursesRequestIdRef = useRef<string | null>(null);
  const backfillRequestIdRef = useRef<string | null>(null);
  const featureImportRequestIdRef = useRef<string | null>(null);
  const visibilityRequestIdRef = useRef<string | null>(null);

  // Search, country, status and page live in the URL, so a reload or a link keeps the exact
  // list an admin was looking at. The URL is the single source of truth — nothing mirrors it
  // into component state.
  const filters = useMemo(
    () => parseCourseListParams({ q, country, status, osm, page }),
    [q, country, status, osm, page],
  );
  const updateFilters = (next: Partial<CourseListFilters>) => {
    router.setParams(courseListParams({ ...filters, ...next }));
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [listCountryPickerOpen, setListCountryPickerOpen] = useState(false);
  const [countryCode, setCountryCode] = useState(DEFAULT_COURSE_ADMIN_COUNTRY_CODE);
  const [withHoleData, setWithHoleData] = useState(false);
  const [importState, setImportState] = useState<ImportState>({ status: 'idle' });
  const [featureImportState, setFeatureImportState] = useState<FeatureImportState>({ status: 'idle' });
  const [coursesState, setCoursesState] = useState<CoursesState>({ status: 'idle' });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [analyzeRowState, setAnalyzeRowState] = useState<Record<string, AnalyzeRowState>>({});
  const [backfillState, setBackfillState] = useState<BackfillState>({ status: 'idle' });
  const [visibilityPickerOpen, setVisibilityPickerOpen] = useState(false);
  const [visibilityState, setVisibilityState] = useState<VisibilityActionState>({ status: 'idle' });
  /** Course id of an in-flight per-card visibility change; that card's chips disable while set. */
  const [cardVisibilityBusy, setCardVisibilityBusy] = useState<string | null>(null);
  const [cardVisibilityError, setCardVisibilityError] = useState<string | null>(null);
  /** Read in the socket listener (subscribed once), so a ref rather than the state above. */
  const cardVisibilityCourseRef = useRef<string | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<CourseRow | null>(null);
  const [holeMapOpen, setHoleMapOpen] = useState(false);
  const [flyoverCommandCourse, setFlyoverCommandCourse] = useState<CourseRow | null>(null);
  const [flyoverCommandCopied, setFlyoverCommandCopied] = useState(false);
  const [flyoverStatus, setFlyoverStatus] = useState<Record<string, FlyoverStatus | null>>({});
  const [flyoverRequest, setFlyoverRequest] = useState<Record<string, { state: FlyoverRequestState; code?: string }>>({});
  const [flyoverShowCommand, setFlyoverShowCommand] = useState(false);
  const [osmRefreshState, setOsmRefreshState] = useState<Record<string, OsmRefreshRowState>>({});
  const osmCounts = useOsmFeatureCounts(
    apiBaseUrl,
    user?.token,
    selectedCourse?.osmType ?? null,
    selectedCourse?.osmId ?? null,
    selectedCourse !== null,
  );

  const fetchCourses = () => {
    const requestId = generateId();
    coursesRequestIdRef.current = requestId;
    setCoursesState({ status: 'loading' });
    socketRef.current.send({ type: 'courseAdmin.courses.list.request', requestId });
  };

  /**
   * Queues satellite analysis for one course, sending the position this row already shows.
   * geodata uses it only when it has no record of the course, to create one — otherwise a
   * course that has never been analysed answers "no geodata record yet" while the line above
   * the button displays the coordinate that would have settled it.
   */
  const startAnalyze = (course: CourseRow) => {
    const courseId = course.id;
    setAnalyzeRowState((prev) => ({ ...prev, [courseId]: { status: 'running' } }));
    socketRef.current.send({
      type: 'courseAdmin.courses.analyze.request',
      requestId: generateId(),
      courseId,
      name: course.name,
      lat: course.lat,
      lon: course.lon,
    });
  };

  /**
   * Re-reads the course's OpenStreetMap data. Plain HTTP rather than the socket: the answer is
   * the delta (written / updated / removed / unchanged), and it comes back in one round trip once
   * Overpass has answered — nothing to stream.
   */
  const refreshOsm = async (course: CourseRow) => {
    const courseId = course.id;
    if (!user?.token) return;
    setOsmRefreshState((prev) => ({ ...prev, [courseId]: { status: 'running' } }));
    try {
      // Send the OSM identity this row already shows (from the catalogue). geodata uses it only
      // when its own row has none — a course whose geodata row was created by the analysis
      // path, say — instead of answering "import it from OpenStreetMap first" while the row
      // right above the button says "OSM relation/N".
      const identity =
        course.osmType && course.osmId
          ? { osmType: course.osmType, osmId: course.osmId, name: course.name }
          : null;
      const response = await fetch(`${apiBaseUrl}/api/geodata/courses/${courseId}/osm/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user.token}`,
          ...(identity ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(identity ? { body: JSON.stringify(identity) } : {}),
      });
      if (!response.ok) {
        setOsmRefreshState((prev) => ({
          ...prev,
          [courseId]: { status: 'failed', httpStatus: response.status },
        }));
        return;
      }
      const body = (await response.json()) as {
        written?: number;
        updated?: number;
        removed?: number;
        unchanged?: number;
        skipped?: number;
      };
      setOsmRefreshState((prev) => ({
        ...prev,
        [courseId]: {
          status: 'done',
          written: body.written ?? 0,
          updated: body.updated ?? 0,
          removed: body.removed ?? 0,
          unchanged: body.unchanged ?? 0,
          skipped: body.skipped ?? 0,
        },
      }));
      // The OSM feature counts on the card come from the list; re-fetch so they reflect it.
      fetchCourses();
    } catch {
      // No answer at all — nothing to name beyond "it did not get through".
      setOsmRefreshState((prev) => ({ ...prev, [courseId]: { status: 'failed', httpStatus: null } }));
    }
  };

  const toggleSelected = (courseId: string) => {
    setSelectedIds((prev) =>
      prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId],
    );
  };

  /**
   * The scripts/render_flyovers.py invocation for one course, ready to paste into a
   * terminal at the repo root. Flyover videos are rendered on a laptop by design (a
   * course is a render-once job, not a pipeline), so the admin surface's contribution
   * is handing over the exact command. Every value is the production one — the STAC
   * catalogues from the course-analysis Pulumi config, the imagery bucket, the
   * Geotorget credential path, this environment's own Keycloak realm — so the command
   * pastes straight into a shell and runs. It carries no credential: the script signs
   * the user in via the OAuth device flow, approved in a browser that is already
   * logged in (Google-federated logins included).
   */
  const flyoverCommandFor = (courseId: string) =>
    [
      'PYTHONPATH=services/course-analysis-service/src \\',
      '  IMAGERY_STAC_URL=https://api.lantmateriet.se/stac-bild/v1 \\',
      '  ELEVATION_STAC_URL=https://api.lantmateriet.se/stac-hojd/v1 \\',
      '  SECRETS_NAME=goffy-Production SECRETS_REGION=eu-west-1 \\',
      '  LANTMATERIET_IMAGE_BUCKET=goffy-course-imagery \\',
      '  LANTMATERIET_IMAGE_REGION=eu-west-1 \\',
      `  GOFFY_AUTH_URL=${KEYCLOAK_BASE} \\`,
      '  python3 scripts/render_flyovers.py \\',
      `    --api-base-url ${apiBaseUrl} \\`,
      `    --course-id ${courseId} \\`,
      '    --output-dir /tmp/flyovers',
    ].join('\n');

  const copyFlyoverCommand = (courseId: string) => {
    if (Platform.OS !== 'web' || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }
    void navigator.clipboard
      .writeText(flyoverCommandFor(courseId))
      .then(() => setFlyoverCommandCopied(true))
      .catch(() => {});
  };

  const requestFlyoverStatus = (courseId: string) => {
    socketRef.current.send({ type: 'courseAdmin.flyovers.status.request', requestId: generateId(), courseId });
  };

  const openFlyoverModal = (course: CourseRow) => {
    setFlyoverCommandCopied(false);
    setFlyoverShowCommand(false);
    // Last visit's outcome, queued or failed, said something about a request that is over. The
    // status request below is what this visit knows.
    setFlyoverRequest((prev) => ({ ...prev, [course.id]: { state: 'idle' } }));
    setFlyoverCommandCourse(course);
    requestFlyoverStatus(course.id);
  };

  /** Queue the render on a batch worker (services/batch-worker); the ack only says "queued". */
  const startFlyoverRender = (courseId: string) => {
    setFlyoverRequest((prev) => ({ ...prev, [courseId]: { state: 'requesting' } }));
    socketRef.current.send({ type: 'courseAdmin.flyovers.render.request', requestId: generateId(), courseId });
  };

  /** "Refresh OSM" for the selected courses: scoped to selectedIds, not the whole catalogue. */
  const startBackfill = () => {
    const requestId = generateId();
    backfillRequestIdRef.current = requestId;
    setBackfillState({ status: 'running', matched: 0, triggeredWithoutMatch: 0, failed: 0 });
    socketRef.current.send({ type: 'courseAdmin.analysis.backfill.request', requestId, courseIds: selectedIds });
  };

  /**
   * Sets one visibility on every selected course. This is the only way to reach a course that is
   * already disabled — the course editor only lets an admin manage official and pending ones, so
   * an OSM-imported course (which lands disabled) could otherwise never be published or made
   * private by anyone but its creator.
   */
  /** One card's chips. Same message and ack path as the bulk action, for a single course. */
  const setVisibilityForCourse = (courseId: string, visibility: SettableVisibility) => {
    const requestId = generateId();
    visibilityRequestIdRef.current = requestId;
    cardVisibilityCourseRef.current = courseId;
    setCardVisibilityBusy(courseId);
    setCardVisibilityError(null);
    socketRef.current.send({
      type: 'courseAdmin.courses.visibility.request',
      requestId,
      courseIds: [courseId],
      visibility,
    });
  };

  const setVisibilityForSelected = (visibility: SettableVisibility) => {
    const requestId = generateId();
    visibilityRequestIdRef.current = requestId;
    cardVisibilityCourseRef.current = null;
    setVisibilityPickerOpen(false);
    setVisibilityState({ status: 'running' });
    socketRef.current.send({
      type: 'courseAdmin.courses.visibility.request',
      requestId,
      courseIds: selectedIds,
      visibility,
    });
  };

  useEffect(() => {
    if (!user?.token) {
      return;
    }
    socketRef.current.setAuthToken(user.token);
  }, [user?.token]);

  useEffect(() => {
    if (!isAdmin || !user?.token) {
      return;
    }
    fetchCourses();
  }, [isAdmin, user?.token]);

  useEffect(() => {
    const unsubscribe = socketRef.current.onMessage((message) => {
      const payload = message as { type?: string; requestId?: string };

      if (payload.type?.startsWith('courseAdmin.courses.list.')) {
        if (payload.requestId !== coursesRequestIdRef.current) {
          return;
        }
        if (payload.type === 'courseAdmin.courses.list.ack') {
          const ack = message as { totalCount?: number; courses?: CourseRow[] };
          setCoursesState({
            status: 'loaded',
            totalCount: ack.totalCount ?? 0,
            // A row from a BFF that predates the OSM-feature lookup carries no counts; treat that
            // as "nothing imported" so the filters and the row line have one shape to handle.
            courses: (ack.courses ?? []).map((course) => ({
              ...course,
              osmFeatures: course.osmFeatures ?? NO_OSM_FEATURES,
            })),
          });
        } else if (payload.type === 'courseAdmin.courses.list.error') {
          setCoursesState({ status: 'error' });
        }
        return;
      }

      if (payload.type?.startsWith('courseAdmin.courses.visibility.')) {
        if (payload.requestId !== visibilityRequestIdRef.current) {
          return;
        }
        if (payload.type === 'courseAdmin.courses.visibility.ack') {
          const ack = message as { visibility?: string; updatedCourseIds?: string[]; failedCount?: number };
          const changed = new Set(ack.updatedCourseIds ?? []);
          const visibility = ack.visibility ?? '';
          // Patch the rows that changed instead of refetching: the list request costs four bulk
          // lookups across two services, and this is the one field it would come back different in.
          setCoursesState((prev) =>
            prev.status === 'loaded'
              ? {
                  ...prev,
                  courses: prev.courses.map((course) =>
                    changed.has(course.id) ? { ...course, visibility } : course,
                  ),
                }
              : prev,
          );
          if (cardVisibilityCourseRef.current) {
            if (changed.size === 0) {
              setCardVisibilityError(cardVisibilityCourseRef.current);
            }
            cardVisibilityCourseRef.current = null;
            setCardVisibilityBusy(null);
          } else {
            setVisibilityState({ status: 'done', updated: changed.size, failed: ack.failedCount ?? 0 });
          }
        } else if (payload.type === 'courseAdmin.courses.visibility.error') {
          if (cardVisibilityCourseRef.current) {
            setCardVisibilityError(cardVisibilityCourseRef.current);
            cardVisibilityCourseRef.current = null;
            setCardVisibilityBusy(null);
          } else {
            setVisibilityState({ status: 'error' });
          }
        }
        return;
      }

      if (payload.type?.startsWith('courseAdmin.analysis.backfill.')) {
        if (payload.requestId !== backfillRequestIdRef.current) {
          return;
        }
        if (payload.type === 'courseAdmin.analysis.backfill.progress') {
          const progress = message as { outcome?: string };
          setBackfillState((prev) => {
            if (prev.status !== 'running') {
              return prev;
            }
            return {
              status: 'running',
              matched: prev.matched + (progress.outcome === 'matched' ? 1 : 0),
              triggeredWithoutMatch: prev.triggeredWithoutMatch + (progress.outcome === 'triggeredWithoutMatch' ? 1 : 0),
              failed: prev.failed + (progress.outcome === 'failed' ? 1 : 0),
            };
          });
        } else if (payload.type === 'courseAdmin.analysis.backfill.ack') {
          const ack = message as {
            matchedCount?: number;
            triggeredWithoutMatchCount?: number;
            failedCount?: number;
          };
          setBackfillState({
            status: 'done',
            matched: ack.matchedCount ?? 0,
            triggeredWithoutMatch: ack.triggeredWithoutMatchCount ?? 0,
            failed: ack.failedCount ?? 0,
          });
          fetchCourses();
        } else if (payload.type === 'courseAdmin.analysis.backfill.error') {
          setBackfillState({ status: 'error' });
        }
        return;
      }

      if (payload.type?.startsWith('courseAdmin.flyovers.')) {
        const flyoverPayload = message as {
          courseId?: string;
          code?: string;
          status?: FlyoverStatus;
        };
        const courseId = flyoverPayload.courseId;
        if (!courseId) {
          return;
        }
        if (payload.type === 'courseAdmin.flyovers.render.ack') {
          setFlyoverRequest((prev) => ({ ...prev, [courseId]: { state: 'queued' } }));
          requestFlyoverStatus(courseId);
        } else if (payload.type === 'courseAdmin.flyovers.render.error') {
          setFlyoverRequest((prev) => ({ ...prev, [courseId]: { state: 'failed', code: flyoverPayload.code } }));
        } else if (payload.type === 'courseAdmin.flyovers.status.response') {
          setFlyoverStatus((prev) => ({ ...prev, [courseId]: flyoverPayload.status ?? null }));
        } else if (payload.type === 'courseAdmin.flyovers.status.error') {
          // Drop what the last successful status said rather than leaving it on screen: a stale
          // "a batch worker is online" outlives the fact it reported and reads as current.
          setFlyoverStatus((prev) => ({ ...prev, [courseId]: null }));
        }
        return;
      }

      // Keyed by courseId rather than requestId: several rows can each have a trigger
      // in flight at once, and the response says which one it answers.
      if (payload.type?.startsWith('courseAdmin.courses.analyze.')) {
        const analyzePayload = message as { courseId?: string; code?: string };
        if (!analyzePayload.courseId) {
          return;
        }
        if (payload.type === 'courseAdmin.courses.analyze.ack') {
          setAnalyzeRowState((prev) => ({ ...prev, [analyzePayload.courseId!]: { status: 'queued' } }));
        } else if (payload.type === 'courseAdmin.courses.analyze.error') {
          setAnalyzeRowState((prev) => ({
            ...prev,
            [analyzePayload.courseId!]: { status: 'failed', code: analyzePayload.code },
          }));
        }
        return;
      }

      if (payload.type?.startsWith('courseAdmin.osm.featureImport.')) {
        if (payload.requestId !== featureImportRequestIdRef.current) {
          return;
        }
        if (payload.type === 'courseAdmin.osm.featureImport.progress') {
          const progress = message as { name?: string; visited?: number; total?: number };
          setFeatureImportState({
            status: 'running',
            visited: progress.visited ?? 0,
            total: progress.total ?? 0,
            name: progress.name ?? null,
          });
        } else if (payload.type === 'courseAdmin.osm.featureImport.ack') {
          const ack = message as {
            totalVisited?: number;
            refreshedCount?: number;
            failedCount?: number;
            skippedCount?: number;
          };
          setFeatureImportState({
            status: 'done',
            totalVisited: ack.totalVisited ?? 0,
            refreshedCount: ack.refreshedCount ?? 0,
            failedCount: ack.failedCount ?? 0,
            skippedCount: ack.skippedCount ?? 0,
          });
          if ((ack.refreshedCount ?? 0) > 0) {
            fetchCourses();
          }
        } else if (payload.type === 'courseAdmin.osm.featureImport.error') {
          setFeatureImportState({ status: 'error' });
        }
        return;
      }

      if (payload.requestId !== requestIdRef.current) {
        return;
      }
      if (payload.type === 'courseAdmin.osm.import.progress') {
        const progress = message as {
          phase?: string;
          totalFoundInCountry?: number;
          checkedCount?: number;
          total?: number;
          importedSoFar?: number;
        };
        if (progress.phase === 'searching') {
          setImportState({ status: 'importing', progress: { phase: 'searching' } });
        } else if (progress.phase === 'found' && typeof progress.totalFoundInCountry === 'number') {
          setImportState({
            status: 'importing',
            progress: { phase: 'found', totalFoundInCountry: progress.totalFoundInCountry },
          });
        } else if (
          progress.phase === 'checkingHoleData' &&
          typeof progress.checkedCount === 'number' &&
          typeof progress.total === 'number'
        ) {
          setImportState({
            status: 'importing',
            progress: {
              phase: 'checkingHoleData',
              checkedCount: progress.checkedCount,
              total: progress.total,
              importedSoFar: progress.importedSoFar ?? 0,
            },
          });
        } else if (progress.phase === 'importing' && typeof progress.total === 'number') {
          setImportState({ status: 'importing', progress: { phase: 'importing', total: progress.total } });
        }
      } else if (payload.type === 'courseAdmin.osm.import.ack') {
        const ack = message as {
          importedCount?: number;
          skippedCount?: number;
          failedCount?: number;
          imported?: ImportedCourse[];
        };
        setImportState({
          status: 'done',
          importedCount: ack.importedCount ?? 0,
          skippedCount: ack.skippedCount ?? 0,
          failedCount: ack.failedCount ?? 0,
          imported: ack.imported ?? [],
        });
        if ((ack.importedCount ?? 0) > 0) {
          fetchCourses();
        }
      } else if (payload.type === 'courseAdmin.osm.import.error') {
        setImportState({ status: 'error' });
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const selectedCountry =
    COURSE_ADMIN_COUNTRIES.find((c) => c.code === countryCode) ?? COURSE_ADMIN_COUNTRIES[0];

  const openModal = () => {
    setImportState({ status: 'idle' });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCountryPickerOpen(false);
  };

  const onImport = () => {
    const requestId = generateId();
    requestIdRef.current = requestId;
    setImportState({ status: 'importing', progress: null });
    socketRef.current.send({
      type: 'courseAdmin.osm.import.request',
      requestId,
      countryCode,
      withHoleData,
    });
  };

  /** Scoped to the modal's country picker, like the course import above it. Which courses in
   * that country still need their OSM features fetched (an identity but no terrain rows yet)
   * is the BFF's decision; the name rides along because the catalogue stores either spelling
   * of a club's country ("Sweden" from OSM imports, "SE" from the club API). */
  const onImportFeatures = () => {
    const requestId = generateId();
    featureImportRequestIdRef.current = requestId;
    setFeatureImportState({ status: 'running', visited: 0, total: 0, name: null });
    socketRef.current.send({
      type: 'courseAdmin.osm.featureImport.request',
      requestId,
      countryCode: selectedCountry.code,
      countryName: selectedCountry.name,
    });
  };

  // Browsing, not just searching: entering the screen shows the courses of the selected country
  // straight away, a page at a time, rather than an empty list until someone types a name.
  const allCourses = coursesState.status === 'loaded' ? coursesState.courses : EMPTY_COURSES;
  const filteredCourses = useMemo(() => filterCourses(allCourses, filters), [allCourses, filters]);
  const currentPage = useMemo(() => pageOf(filteredCourses, filters.page), [filteredCourses, filters.page]);
  const searchResults = currentPage.items;

  const selectedListCountry =
    filters.countryCode === ALL_COUNTRIES
      ? null
      : (COURSE_ADMIN_COUNTRIES.find((c) => c.code === filters.countryCode) ?? null);

  // "All shown" means the page in front of the admin, not the whole filtered set — the two are
  // the same only until a filter matches more than one page.
  const allShownSelected =
    searchResults.length > 0 && searchResults.every((course) => selectedIds.includes(course.id));
  const toggleAllShown = () => {
    const shownIds = searchResults.map((course) => course.id);
    setSelectedIds((prev) =>
      allShownSelected
        ? prev.filter((id) => !shownIds.includes(id))
        : [...prev, ...shownIds.filter((id) => !prev.includes(id))],
    );
  };
  const analyzeSelected = () => {
    // By course rather than by id: the request carries the row's position, and a selected id
    // whose row is no longer loaded has none to send.
    allCourses.filter((course) => selectedIds.includes(course.id)).forEach(startAnalyze);
  };
  // One request per course, same as analyzeSelected: the worker takes one job at a time and the
  // queue is durable, so fanning out here is no worse than a batch message would be.
  const flyoverSelected = () => {
    selectedIds.forEach(startFlyoverRender);
  };

  return (
    <>
      <Stack.Screen options={{ title: t('golfCourseAdminPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('golfCourseAdminPage.title')}</Text>

        {!isAdmin ? (
          <Text style={styles.paragraph}>{t('golfCourseAdminPage.forbidden')}</Text>
        ) : (
          <>
            {coursesState.status === 'loaded' ? (
              <Text testID="golf-course-admin-total-count" style={styles.paragraph}>
                {t('golfCourseAdminPage.totalCourses', { count: String(coursesState.totalCount) })}
              </Text>
            ) : coursesState.status === 'loading' ? (
              <View style={styles.loadingRow}>
                <ActivityIndicator size="small" color={appColors.text.primary} />
                <Text style={styles.paragraph}>{t('golfCourseAdminPage.loadingCourses')}</Text>
              </View>
            ) : coursesState.status === 'error' ? (
              <Text style={styles.errorText}>{t('golfCourseAdminPage.loadCoursesError')}</Text>
            ) : null}

            <Pressable
              testID="golf-course-admin-find-osm"
              accessibilityRole="button"
              style={styles.scanButton}
              onPress={openModal}
            >
              <Ionicons name="search" size={18} color={appColors.text.primary} />
              <Text style={styles.scanButtonText}>{t('golfCourseAdminPage.findOsmButton')}</Text>
            </Pressable>

            <View style={styles.searchRow}>
              <Ionicons name="search" size={16} color={appColors.text.secondary} />
              <TextInput
                testID="golf-course-admin-search-input"
                style={styles.searchInput}
                value={filters.query}
                onChangeText={(text) => updateFilters({ query: text, page: 1 })}
                placeholder={t('golfCourseAdminPage.searchPlaceholder')}
                placeholderTextColor={appColors.text.secondary}
                autoCorrect={false}
                autoCapitalize="none"
              />
              {filters.query.length > 0 ? (
                <Pressable
                  testID="golf-course-admin-search-clear"
                  accessibilityRole="button"
                  onPress={() => updateFilters({ query: '', page: 1 })}
                  hitSlop={8}
                >
                  <Ionicons name="close-circle" size={16} color={appColors.text.secondary} />
                </Pressable>
              ) : null}
            </View>

            <Pressable
              testID="golf-course-admin-list-country"
              accessibilityRole="button"
              style={styles.listCountryRow}
              onPress={() => setListCountryPickerOpen(true)}
            >
              <Ionicons name="flag-outline" size={16} color={appColors.text.secondary} />
              <Text style={styles.officialLabel}>{t('golfCourseAdminPage.countryLabel')}</Text>
              <Text testID="golf-course-admin-list-country-value" style={styles.pickerOptionText}>
                {selectedListCountry?.name ?? t('golfCourseAdminPage.countryAll')}
              </Text>
              <Ionicons name="chevron-down" size={16} color={appColors.text.secondary} />
            </Pressable>

            <View style={styles.filterRow}>
              {(['all', 'analysed', 'notAnalysed'] as const).map((filter) => (
                <Pressable
                  key={filter}
                  testID={`golf-course-admin-filter-${filter}`}
                  accessibilityRole="button"
                  accessibilityState={{ selected: filters.analysisFilter === filter }}
                  style={[styles.filterChip, filters.analysisFilter === filter && styles.filterChipActive]}
                  onPress={() => updateFilters({ analysisFilter: filter as AnalysisFilter, page: 1 })}
                >
                  <Text
                    style={[
                      styles.filterChipText,
                      filters.analysisFilter === filter && styles.filterChipTextActive,
                    ]}
                  >
                    {t(`golfCourseAdminPage.filter${filter[0].toUpperCase()}${filter.slice(1)}`)}
                  </Text>
                </Pressable>
              ))}
            </View>

            {/* What the OSM import actually stored, not what OSM holds — a course can be matched
                to OSM and still have none of these. */}
            <View style={styles.filterRow}>
              {OSM_FEATURE_KINDS.map((kind) => {
                const active = filters.osmFeatures.includes(kind);
                return (
                  <Pressable
                    key={kind}
                    testID={`golf-course-admin-osm-filter-${kind}`}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: active }}
                    style={styles.osmFilterChip}
                    onPress={() =>
                      updateFilters({
                        osmFeatures: active
                          ? filters.osmFeatures.filter((existing) => existing !== kind)
                          : [...filters.osmFeatures, kind],
                        page: 1,
                      })
                    }
                  >
                    <View style={[styles.smallCheckbox, active && styles.checkboxChecked]}>
                      {active ? <Text style={styles.smallCheckboxMark}>✓</Text> : null}
                    </View>
                    <Text style={[styles.filterChipText, active && styles.filterChipTextActive]}>
                      {t(`golfCourseAdminPage.osmFilter${kind[0].toUpperCase()}${kind.slice(1)}`)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            {searchResults.length > 0 ? (
              <View style={styles.selectionRow}>
                <Pressable
                  testID="golf-course-admin-select-all"
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: allShownSelected }}
                  style={styles.selectAllRow}
                  onPress={toggleAllShown}
                >
                  <View style={[styles.checkbox, allShownSelected && styles.checkboxChecked]}>
                    {allShownSelected ? <Text style={styles.checkboxMark}>✓</Text> : null}
                  </View>
                  <Text style={styles.paragraph}>{t('golfCourseAdminPage.selectAllShown')}</Text>
                </Pressable>
                {selectedIds.length > 0 ? (
                  <>
                    <Text testID="golf-course-admin-selected-count" style={styles.progressText}>
                      {t('golfCourseAdminPage.selectedCount', { count: String(selectedIds.length) })}
                    </Text>
                    <Pressable
                      testID="golf-course-admin-analyze-selected"
                      accessibilityRole="button"
                      style={styles.analyzeButton}
                      onPress={analyzeSelected}
                    >
                      <Text style={styles.analyzeButtonText}>
                        {t('golfCourseAdminPage.analyzeSelected')}
                      </Text>
                    </Pressable>
                    <Pressable
                      testID="golf-course-admin-flyover-selected"
                      accessibilityRole="button"
                      style={styles.analyzeButton}
                      onPress={flyoverSelected}
                    >
                      <Text style={styles.analyzeButtonText}>
                        {t('golfCourseAdminPage.flyoverSelected')}
                      </Text>
                    </Pressable>
                    <Pressable
                      testID="golf-course-admin-backfill"
                      accessibilityRole="button"
                      disabled={backfillState.status === 'running'}
                      style={[styles.analyzeButton, backfillState.status === 'running' && styles.actionButtonDisabled]}
                      onPress={startBackfill}
                    >
                      {backfillState.status === 'running' ? <ActivityIndicator color={appColors.text.primary} /> : null}
                      <Text style={styles.analyzeButtonText}>
                        {backfillState.status === 'running'
                          ? t('golfCourseAdminPage.backfillRunning')
                          : t('golfCourseAdminPage.backfillButton')}
                      </Text>
                    </Pressable>
                    <Pressable
                      testID="golf-course-admin-set-visibility"
                      accessibilityRole="button"
                      disabled={visibilityState.status === 'running'}
                      style={[styles.analyzeButton, visibilityState.status === 'running' && styles.actionButtonDisabled]}
                      onPress={() => setVisibilityPickerOpen(true)}
                    >
                      {visibilityState.status === 'running' ? (
                        <ActivityIndicator color={appColors.text.primary} />
                      ) : null}
                      <Text style={styles.analyzeButtonText}>
                        {t('golfCourseAdminPage.setVisibility')}
                      </Text>
                    </Pressable>
                    <Pressable
                      testID="golf-course-admin-clear-selection"
                      accessibilityRole="button"
                      style={styles.analyzeButton}
                      onPress={() => setSelectedIds([])}
                    >
                      <Text style={styles.analyzeButtonText}>
                        {t('golfCourseAdminPage.clearSelection')}
                      </Text>
                    </Pressable>
                  </>
                ) : null}
              </View>
            ) : null}

            {backfillState.status === 'running' || backfillState.status === 'done' ? (
              <Text
                testID={
                  backfillState.status === 'running'
                    ? 'golf-course-admin-backfill-progress'
                    : 'golf-course-admin-backfill-result'
                }
                style={styles.progressText}
              >
                {t(
                  backfillState.status === 'running'
                    ? 'golfCourseAdminPage.backfillProgress'
                    : 'golfCourseAdminPage.backfillDone',
                  {
                    matched: String(backfillState.matched),
                    triggered: String(backfillState.triggeredWithoutMatch),
                    failed: String(backfillState.failed),
                  },
                )}
              </Text>
            ) : backfillState.status === 'error' ? (
              <Text testID="golf-course-admin-backfill-error" style={styles.errorText}>
                {t('golfCourseAdminPage.backfillError')}
              </Text>
            ) : null}

            {visibilityState.status === 'done' ? (
              <Text testID="golf-course-admin-visibility-result" style={styles.progressText}>
                {t('golfCourseAdminPage.visibilityDone', {
                  updated: String(visibilityState.updated),
                  failed: String(visibilityState.failed),
                })}
              </Text>
            ) : visibilityState.status === 'error' ? (
              <Text testID="golf-course-admin-visibility-error" style={styles.errorText}>
                {t('golfCourseAdminPage.visibilityError')}
              </Text>
            ) : null}

            {coursesState.status === 'loaded' ? (
              searchResults.length === 0 ? (
                <Text style={styles.paragraph}>{t('golfCourseAdminPage.searchNoMatches')}</Text>
              ) : (
                <View style={styles.searchResults}>
                  {searchResults.map((course) => (
                    <Pressable
                      key={course.id}
                      testID="golf-course-admin-search-result"
                      accessibilityRole="button"
                      style={styles.courseCard}
                      onPress={() => setSelectedCourse(course)}
                    >
                      <View style={styles.courseCardHeader}>
                        <Pressable
                          testID="golf-course-admin-select-course"
                          accessibilityRole="checkbox"
                          accessibilityState={{ checked: selectedIds.includes(course.id) }}
                          hitSlop={8}
                          onPress={(event) => {
                            event.stopPropagation();
                            toggleSelected(course.id);
                          }}
                        >
                          <View
                            style={[styles.checkbox, selectedIds.includes(course.id) && styles.checkboxChecked]}
                          >
                            {selectedIds.includes(course.id) ? (
                              <Text style={styles.checkboxMark}>✓</Text>
                            ) : null}
                          </View>
                        </Pressable>
                        <Text style={styles.courseCardName}>{course.name}</Text>
                      </View>
                      <Text style={styles.courseCardMeta}>
                        {[course.clubName, course.city, course.country].filter(Boolean).join(' · ')}
                      </Text>
                      <Text style={styles.courseCardMeta}>
                        {t('golfCourseAdminPage.courseCardDetails', {
                          par: String(course.par),
                          holes: String(course.holesCount),
                          visibility: course.visibility,
                        })}
                      </Text>
                      <View style={styles.visibilityChipRow}>
                        {SETTABLE_VISIBILITIES.map((visibility) => {
                          const active = course.visibility === visibility;
                          const busy = cardVisibilityBusy === course.id;
                          return (
                            <Pressable
                              key={visibility}
                              testID={`course-visibility-${visibility}-${course.id}`}
                              accessibilityRole="button"
                              accessibilityState={{ selected: active, disabled: active || busy }}
                              disabled={active || busy}
                              hitSlop={4}
                              style={[
                                styles.visibilityChip,
                                active && styles.visibilityChipActive,
                                busy && styles.actionButtonDisabled,
                              ]}
                              onPress={(event) => {
                                event.stopPropagation();
                                setVisibilityForCourse(course.id, visibility);
                              }}
                            >
                              <Text style={[styles.visibilityChipText, active && styles.visibilityChipTextActive]}>
                                {t(`golfCourseAdminPage.visibilityChip${visibility[0].toUpperCase()}${visibility.slice(1)}`)}
                              </Text>
                            </Pressable>
                          );
                        })}
                        {cardVisibilityBusy === course.id ? (
                          <ActivityIndicator size="small" color={appColors.text.primary} />
                        ) : null}
                      </View>
                      {cardVisibilityError === course.id ? (
                        <Text style={styles.errorText}>{t('golfCourseAdminPage.visibilityError')}</Text>
                      ) : null}
                      {typeof course.lat === 'number' && typeof course.lon === 'number' ? (
                        <View style={styles.coordinateRow}>
                          <Text style={styles.courseCardMeta}>
                            {t('golfCourseAdminPage.courseCardCoordinates', {
                              lat: course.lat.toFixed(5),
                              lon: course.lon.toFixed(5),
                            })}
                          </Text>
                          <Pressable
                            testID="golf-course-admin-open-in-esri"
                            accessibilityRole="link"
                            hitSlop={8}
                            onPress={(event) => {
                              event.stopPropagation();
                              void Linking.openURL(esriImageryUrl(course.lat as number, course.lon as number));
                            }}
                          >
                            <Text style={styles.inlineLink}>{t('golfCourseAdminPage.openInEsri')}</Text>
                          </Pressable>
                          <Pressable
                            testID="golf-course-admin-open-in-osm"
                            accessibilityRole="link"
                            hitSlop={8}
                            onPress={(event) => {
                              event.stopPropagation();
                              void Linking.openURL(osmUrl(course));
                            }}
                          >
                            <Text style={styles.inlineLink}>{t('golfCourseAdminPage.openInOsm')}</Text>
                          </Pressable>
                        </View>
                      ) : null}

                      <View style={styles.analysisRow}>
                        <View
                          style={[
                            styles.analysisBadge,
                            course.analysisState === 'analysed'
                              ? styles.analysisBadgeGood
                              : course.analysisState === 'abandoned' || course.analysisState === 'disabled'
                                ? styles.analysisBadgeBad
                                : styles.analysisBadgeWarn,
                          ]}
                        >
                          <Text testID="golf-course-admin-analysis-badge" style={styles.analysisBadgeText}>
                            {course.analysisState
                              ? t(`courseAnalysis.status.state.${course.analysisState}`)
                              : t('golfCourseAdminPage.analysisUnknown')}
                          </Text>
                        </View>
                        <Pressable
                          testID="golf-course-admin-analyze-button"
                          accessibilityRole="button"
                          disabled={
                            analyzeRowState[course.id]?.status === 'running' || course.analysisState === 'noGeodata'
                          }
                          style={[
                            styles.analyzeButton,
                            (analyzeRowState[course.id]?.status === 'running' ||
                              course.analysisState === 'noGeodata') &&
                              styles.actionButtonDisabled,
                          ]}
                          onPress={(event) => {
                            event.stopPropagation();
                            startAnalyze(course);
                          }}
                        >
                          <Text style={styles.analyzeButtonText}>
                            {t(
                              analyzeRowState[course.id]?.status === 'running'
                                ? 'courseAnalysis.status.running'
                                : 'courseAnalysis.status.run',
                            )}
                          </Text>
                        </Pressable>
                        <Pressable
                          testID="golf-course-admin-flyover-button"
                          accessibilityRole="button"
                          disabled={course.analysisState === 'noGeodata' || course.flyableHoles === 0}
                          aria-disabled={course.analysisState === 'noGeodata' || course.flyableHoles === 0}
                          style={[
                            styles.analyzeButton,
                            (course.analysisState === 'noGeodata' || course.flyableHoles === 0) &&
                              styles.actionButtonDisabled,
                          ]}
                          onPress={(event) => {
                            event.stopPropagation();
                            openFlyoverModal(course);
                          }}
                        >
                          <Text style={styles.analyzeButtonText}>
                            {t('golfCourseAdminPage.flyoverButton')}
                          </Text>
                        </Pressable>
                        <Pressable
                          testID="golf-course-admin-osm-refresh-button"
                          accessibilityRole="button"
                          disabled={
                            !course.osmType || !course.osmId || osmRefreshState[course.id]?.status === 'running'
                          }
                          style={[
                            styles.analyzeButton,
                            (!course.osmType || !course.osmId || osmRefreshState[course.id]?.status === 'running') &&
                              styles.actionButtonDisabled,
                          ]}
                          onPress={(event) => {
                            event.stopPropagation();
                            void refreshOsm(course);
                          }}
                        >
                          <Text style={styles.analyzeButtonText}>
                            {t(
                              osmRefreshState[course.id]?.status === 'running'
                                ? 'golfCourseAdminPage.osmRefreshRunning'
                                : 'golfCourseAdminPage.osmRefreshButton',
                            )}
                          </Text>
                        </Pressable>
                      </View>
                      {((refresh) =>
                        refresh?.status === 'done' ? (
                          <Text testID="golf-course-admin-osm-refresh-status" style={styles.analyzeQueuedText}>
                            {t('golfCourseAdminPage.osmRefreshResult', {
                              written: String(refresh.written),
                              updated: String(refresh.updated),
                              removed: String(refresh.removed),
                              unchanged: String(refresh.unchanged),
                            })}
                          </Text>
                        ) : null)(osmRefreshState[course.id])}
                      {((refresh) =>
                        refresh?.status === 'failed'
                          ? ((failure) => (
                              <Text testID="golf-course-admin-osm-refresh-status" style={styles.errorText}>
                                {t(failure.key, failure.params)}
                              </Text>
                            ))(osmRefreshFailure(refresh.httpStatus))
                          : null)(osmRefreshState[course.id])}
                      {analyzeRowState[course.id]?.status === 'queued' ? (
                        <Text testID="golf-course-admin-analyze-status" style={styles.analyzeQueuedText}>
                          {t('courseAnalysis.status.queued')}
                        </Text>
                      ) : ((analyze) =>
                          analyze?.status === 'failed' ? (
                            <Text testID="golf-course-admin-analyze-status" style={styles.errorText}>
                              {t(analyzeFailureKey(analyze.code))}
                            </Text>
                          ) : null)(analyzeRowState[course.id])}
                      {course.analysisState === 'noGeodata' ? (
                        <Text testID="golf-course-admin-no-geodata-hint" style={styles.courseCardOsm}>
                          {t('golfCourseAdminPage.actionNoGeodata')}
                        </Text>
                      ) : course.flyableHoles === 0 ? (
                        // Why the flyover button above is greyed out: no hole has both a tee and a
                        // green, so a render would refuse. The no-geodata hint already covers the
                        // wider case, so only one of the two ever shows.
                        <Text testID="golf-course-admin-nothing-to-fly-hint" style={styles.courseCardOsm}>
                          {t('golfCourseAdminPage.flyoverNothingToFly')}
                        </Text>
                      ) : null}

                      {course.osmType && course.osmId ? (
                        <Text style={styles.courseCardOsm}>
                          {t('golfCourseAdminPage.courseCardOsm', {
                            osmType: course.osmType,
                            osmId: String(course.osmId),
                          })}
                        </Text>
                      ) : null}

                      <Text testID="golf-course-admin-osm-features" style={styles.courseCardOsm}>
                        {t('golfCourseAdminPage.courseCardOsmFeatures', {
                          greens: String(course.osmFeatures.greens),
                          holes: String(course.osmFeatures.holes),
                          bunkers: String(course.osmFeatures.bunkers),
                          fairways: String(course.osmFeatures.fairways),
                        })}
                      </Text>

                      {course.scorecardImport ? (
                        <Text
                          testID="golf-course-admin-scorecard-import"
                          style={styles.courseCardOsm}
                        >
                          {t('golfCourseAdminPage.courseCardScorecardImport', {
                            holes: String(course.scorecardImport.holes),
                            tees: String(course.scorecardImport.tees),
                            date: formatImportDate(course.scorecardImport.importedAtUtc),
                          })}
                        </Text>
                      ) : null}

                      {course.flyover ? (
                        <Text
                          testID="golf-course-admin-flyover-state"
                          style={
                            course.flyover.rendered
                              ? styles.flyoverStateRendered
                              : course.flyover.latestStatus === 'failed'
                                ? styles.errorText
                                : styles.courseCardOsm
                          }
                        >
                          {course.flyover.rendered
                            ? t('golfCourseAdminPage.flyoverStateRendered', {
                                holes: String(course.flyover.renderedHoles),
                              })
                            : course.flyover.latestStatus === 'failed'
                              ? t('golfCourseAdminPage.flyoverStateFailed')
                              : t('golfCourseAdminPage.flyoverStateQueued')}
                        </Text>
                      ) : null}
                    </Pressable>
                  ))}
                </View>
              )
            ) : null}

            {coursesState.status === 'loaded' && currentPage.total > 0 ? (
              <View style={styles.pagerRow}>
                <Pressable
                  testID="golf-course-admin-page-prev"
                  accessibilityRole="button"
                  disabled={currentPage.page <= 1}
                  style={[styles.pagerButton, currentPage.page <= 1 && styles.actionButtonDisabled]}
                  onPress={() => updateFilters({ page: currentPage.page - 1 })}
                >
                  <Text style={styles.analyzeButtonText}>{t('golfCourseAdminPage.previousPage')}</Text>
                </Pressable>
                <Text testID="golf-course-admin-page-status" style={styles.progressText}>
                  {t('golfCourseAdminPage.pageRange', {
                    from: String(currentPage.from),
                    to: String(currentPage.to),
                    total: String(currentPage.total),
                    page: String(currentPage.page),
                    pages: String(currentPage.pageCount),
                  })}
                </Text>
                <Pressable
                  testID="golf-course-admin-page-next"
                  accessibilityRole="button"
                  disabled={currentPage.page >= currentPage.pageCount}
                  style={[
                    styles.pagerButton,
                    currentPage.page >= currentPage.pageCount && styles.actionButtonDisabled,
                  ]}
                  onPress={() => updateFilters({ page: currentPage.page + 1 })}
                >
                  <Text style={styles.analyzeButtonText}>{t('golfCourseAdminPage.nextPage')}</Text>
                </Pressable>
              </View>
            ) : null}

          </>
        )}
      </ScrollView>

      <Modal
        visible={visibilityPickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setVisibilityPickerOpen(false)}
      >
        <Pressable
          style={[styles.pickerOverlay, styles.osmModalOverlay]}
          onPress={() => setVisibilityPickerOpen(false)}
        >
          <Pressable style={styles.osmModalCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.osmModalHeader}>
              <Text style={styles.osmPickerTitle}>
                {t('golfCourseAdminPage.visibilityPickerTitle', { count: String(selectedIds.length) })}
              </Text>
              <Pressable
                testID="golf-course-admin-visibility-close"
                accessibilityRole="button"
                accessibilityLabel={t('golfCourseAdminPage.close')}
                hitSlop={8}
                style={styles.osmModalClose}
                onPress={() => setVisibilityPickerOpen(false)}
              >
                <Ionicons name="close" size={20} color={appColors.text.primary} />
              </Pressable>
            </View>
            {SETTABLE_VISIBILITIES.map((visibility) => (
              <Pressable
                key={visibility}
                testID={`golf-course-admin-visibility-${visibility}`}
                accessibilityRole="button"
                style={styles.pickerOption}
                onPress={() => setVisibilityForSelected(visibility)}
              >
                <Text style={styles.pickerOptionText}>
                  {t(`golfCourseAdminPage.visibility${visibility[0].toUpperCase()}${visibility.slice(1)}`)}
                </Text>
              </Pressable>
            ))}
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={listCountryPickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setListCountryPickerOpen(false)}
      >
        <Pressable
          style={[styles.pickerOverlay, styles.osmModalOverlay]}
          onPress={() => setListCountryPickerOpen(false)}
        >
          <Pressable style={styles.osmModalCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.osmModalHeader}>
              <Text style={styles.osmPickerTitle}>{t('golfCourseAdminPage.countryLabel')}</Text>
              <Pressable
                testID="golf-course-admin-list-country-close"
                accessibilityRole="button"
                accessibilityLabel={t('golfCourseAdminPage.close')}
                hitSlop={8}
                style={styles.osmModalClose}
                onPress={() => setListCountryPickerOpen(false)}
              >
                <Ionicons name="close" size={20} color={appColors.text.primary} />
              </Pressable>
            </View>
            <ScrollView style={styles.countryList}>
              {[{ code: ALL_COUNTRIES, name: t('golfCourseAdminPage.countryAll') }, ...COURSE_ADMIN_COUNTRIES].map(
                (country) => (
                  <Pressable
                    key={country.code}
                    testID={`golf-course-admin-list-country-${country.code}`}
                    style={styles.pickerOption}
                    onPress={() => {
                      updateFilters({ countryCode: country.code, page: 1 });
                      setListCountryPickerOpen(false);
                    }}
                  >
                    <Text style={styles.pickerOptionText}>{country.name}</Text>
                  </Pressable>
                ),
              )}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={flyoverCommandCourse !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setFlyoverCommandCourse(null)}
      >
        <Pressable
          style={[styles.pickerOverlay, styles.osmModalOverlay]}
          onPress={() => setFlyoverCommandCourse(null)}
        >
          <Pressable style={styles.osmModalCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.osmModalHeader}>
              <Text style={styles.osmPickerTitle}>
                {t('golfCourseAdminPage.flyoverModalTitle', {
                  name: flyoverCommandCourse?.name ?? '',
                })}
              </Text>
              <Pressable
                testID="golf-course-admin-flyover-close"
                accessibilityRole="button"
                accessibilityLabel={t('golfCourseAdminPage.close')}
                hitSlop={8}
                style={styles.osmModalClose}
                onPress={() => setFlyoverCommandCourse(null)}
              >
                <Ionicons name="close" size={20} color={appColors.text.primary} />
              </Pressable>
            </View>
            <View style={styles.osmModalBody}>
              <Text style={styles.paragraph}>{t('golfCourseAdminPage.flyoverWorkerExplainer')}</Text>
              {flyoverCommandCourse ? (
                <FlyoverJobStatusBlock
                  status={flyoverStatus[flyoverCommandCourse.id]}
                  request={flyoverRequest[flyoverCommandCourse.id]}
                  onRender={() => startFlyoverRender(flyoverCommandCourse.id)}
                />
              ) : null}
              <Pressable
                testID="golf-course-admin-flyover-toggle-command"
                accessibilityRole="button"
                onPress={() => setFlyoverShowCommand((prev) => !prev)}
              >
                <Text style={styles.linkText}>
                  {flyoverShowCommand
                    ? t('golfCourseAdminPage.flyoverHideCommand')
                    : t('golfCourseAdminPage.flyoverShowCommand')}
                </Text>
              </Pressable>
              {flyoverShowCommand ? (
                <>
              <Text style={styles.paragraph}>{t('golfCourseAdminPage.flyoverExplainer')}</Text>
              <ScrollView horizontal style={styles.flyoverCommandBox}>
                <Text
                  testID="golf-course-admin-flyover-command"
                  selectable
                  style={styles.flyoverCommandText}
                >
                  {flyoverCommandCourse ? flyoverCommandFor(flyoverCommandCourse.id) : ''}
                </Text>
              </ScrollView>
              {Platform.OS === 'web' ? (
                <View style={styles.flyoverCopyRow}>
                  <Pressable
                    testID="golf-course-admin-flyover-copy"
                    accessibilityRole="button"
                    style={styles.analyzeButton}
                    onPress={() =>
                      flyoverCommandCourse && copyFlyoverCommand(flyoverCommandCourse.id)
                    }
                  >
                    <Text style={styles.analyzeButtonText}>
                      {t('golfCourseAdminPage.flyoverCopy')}
                    </Text>
                  </Pressable>
                  {flyoverCommandCopied ? (
                    <Text testID="golf-course-admin-flyover-copied" style={styles.statusText}>
                      {t('golfCourseAdminPage.flyoverCopied')}
                    </Text>
                  ) : null}
                </View>
              ) : null}
                </>
              ) : null}
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal visible={modalOpen} transparent animationType="fade" onRequestClose={closeModal}>
        <Pressable style={[styles.pickerOverlay, styles.osmModalOverlay]} onPress={closeModal}>
          <Pressable style={styles.osmModalCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.osmModalHeader}>
              <Text style={styles.osmPickerTitle}>{t('golfCourseAdminPage.modalTitle')}</Text>
              <Pressable
                testID="golf-course-admin-modal-close"
                accessibilityRole="button"
                accessibilityLabel={t('golfCourseAdminPage.close')}
                hitSlop={8}
                style={styles.osmModalClose}
                onPress={closeModal}
              >
                <Ionicons name="close" size={20} color={appColors.text.primary} />
              </Pressable>
            </View>

            <View style={styles.osmModalBody}>
              {countryPickerOpen ? (
                <ScrollView style={styles.countryList}>
                  {COURSE_ADMIN_COUNTRIES.map((country) => (
                    <Pressable
                      key={country.code}
                      testID={`golf-course-admin-country-${country.code}`}
                      style={styles.pickerOption}
                      onPress={() => {
                        setCountryCode(country.code);
                        setCountryPickerOpen(false);
                      }}
                    >
                      <Text style={styles.pickerOptionText}>{country.name}</Text>
                    </Pressable>
                  ))}
                </ScrollView>
              ) : importState.status === 'done' ? (
                <>
                  <Text style={styles.statusText}>
                    {t('golfCourseAdminPage.resultSummary', {
                      imported: String(importState.importedCount),
                      skipped: String(importState.skippedCount),
                    })}
                  </Text>
                  {importState.failedCount > 0 ? (
                    <Text style={styles.errorText}>
                      {t('golfCourseAdminPage.resultFailed', { failed: String(importState.failedCount) })}
                    </Text>
                  ) : null}
                  {importState.imported.length > 0 ? (
                    <>
                      <Text style={styles.importedListTitle}>{t('golfCourseAdminPage.importedListTitle')}</Text>
                      <ScrollView style={styles.importedList}>
                        {importState.imported.map((course) => (
                          <Text key={course.courseId} style={styles.importedListItem}>
                            {course.name}
                          </Text>
                        ))}
                      </ScrollView>
                    </>
                  ) : null}
                  <Pressable
                    testID="golf-course-admin-import-again"
                    accessibilityRole="button"
                    style={styles.scanButton}
                    onPress={() => setImportState({ status: 'idle' })}
                  >
                    <Text style={styles.scanButtonText}>{t('golfCourseAdminPage.importButton')}</Text>
                  </Pressable>
                </>
              ) : (
                <>
                  <Pressable
                    testID="golf-course-admin-country-row"
                    style={styles.countryRow}
                    onPress={() => setCountryPickerOpen(true)}
                  >
                    <Text style={styles.officialLabel}>{t('golfCourseAdminPage.countryLabel')}</Text>
                    <Text style={styles.pickerOptionText}>{selectedCountry.name}</Text>
                  </Pressable>

                  <Pressable
                    style={styles.officialRow}
                    onPress={() => setWithHoleData((prev) => !prev)}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: withHoleData }}
                  >
                    <View style={[styles.checkbox, withHoleData && styles.checkboxChecked]}>
                      {withHoleData ? <Text style={styles.checkboxMark}>✓</Text> : null}
                    </View>
                    <Text style={styles.officialLabel}>{t('golfCourseAdminPage.withHoleDataLabel')}</Text>
                  </Pressable>

                  <Pressable
                    testID="golf-course-admin-import"
                    accessibilityRole="button"
                    style={[styles.scanButton, importState.status === 'importing' && styles.actionButtonDisabled]}
                    disabled={importState.status === 'importing'}
                    onPress={onImport}
                  >
                    {importState.status === 'importing' ? (
                      <ActivityIndicator color={appColors.text.primary} />
                    ) : (
                      <Ionicons name="cloud-download-outline" size={18} color={appColors.text.primary} />
                    )}
                    <Text style={styles.scanButtonText}>
                      {importState.status === 'importing'
                        ? t('golfCourseAdminPage.importing')
                        : t('golfCourseAdminPage.importButton')}
                    </Text>
                  </Pressable>

                  {importState.status === 'importing' && importState.progress ? (
                    <Text testID="golf-course-admin-progress" style={styles.progressText}>
                      {describeProgress(importState.progress, t)}
                    </Text>
                  ) : null}

                  {importState.status === 'error' ? (
                    <Text testID="golf-course-admin-error" style={styles.errorText}>
                      {t('golfCourseAdminPage.error')}
                    </Text>
                  ) : null}

                  <Text style={styles.detailSectionTitle}>
                    {t('golfCourseAdminPage.featureImportTitle')}
                  </Text>
                  <Text style={styles.courseCardMeta}>{t('golfCourseAdminPage.featureImportHint')}</Text>
                  <Pressable
                    testID="golf-course-admin-feature-import"
                    accessibilityRole="button"
                    style={[
                      styles.scanButton,
                      featureImportState.status === 'running' && styles.actionButtonDisabled,
                    ]}
                    disabled={featureImportState.status === 'running'}
                    onPress={onImportFeatures}
                  >
                    {featureImportState.status === 'running' ? (
                      <ActivityIndicator color={appColors.text.primary} />
                    ) : (
                      <Ionicons name="golf-outline" size={18} color={appColors.text.primary} />
                    )}
                    <Text style={styles.scanButtonText}>
                      {featureImportState.status === 'running'
                        ? t('golfCourseAdminPage.featureImportRunning')
                        : t('golfCourseAdminPage.featureImportButton')}
                    </Text>
                  </Pressable>

                  {featureImportState.status === 'running' && featureImportState.total > 0 ? (
                    <Text testID="golf-course-admin-feature-import-progress" style={styles.progressText}>
                      {t('golfCourseAdminPage.featureImportProgress', {
                        visited: String(featureImportState.visited),
                        total: String(featureImportState.total),
                        name: featureImportState.name ?? '',
                      })}
                    </Text>
                  ) : null}

                  {featureImportState.status === 'done' ? (
                    <>
                      <Text testID="golf-course-admin-feature-import-result" style={styles.statusText}>
                        {t('golfCourseAdminPage.featureImportDone', {
                          refreshed: String(featureImportState.refreshedCount),
                          visited: String(featureImportState.totalVisited),
                          skipped: String(featureImportState.skippedCount),
                        })}
                      </Text>
                      {featureImportState.failedCount > 0 ? (
                        <Text style={styles.errorText}>
                          {t('golfCourseAdminPage.featureImportFailed', {
                            failed: String(featureImportState.failedCount),
                          })}
                        </Text>
                      ) : null}
                    </>
                  ) : null}

                  {featureImportState.status === 'error' ? (
                    <Text testID="golf-course-admin-feature-import-error" style={styles.errorText}>
                      {t('golfCourseAdminPage.featureImportError')}
                    </Text>
                  ) : null}
                </>
              )}
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={selectedCourse !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setSelectedCourse(null)}
      >
        <Pressable
          style={[styles.pickerOverlay, styles.osmModalOverlay]}
          onPress={() => setSelectedCourse(null)}
        >
          <Pressable style={styles.osmModalCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.osmModalHeader}>
              <Text style={styles.osmPickerTitle}>{selectedCourse?.name}</Text>
              <Pressable
                testID="golf-course-admin-detail-close"
                accessibilityRole="button"
                accessibilityLabel={t('golfCourseAdminPage.close')}
                hitSlop={8}
                style={styles.osmModalClose}
                onPress={() => setSelectedCourse(null)}
              >
                <Ionicons name="close" size={20} color={appColors.text.primary} />
              </Pressable>
            </View>

            {selectedCourse ? (
              <ScrollView style={styles.osmModalBody}>
                <Text style={styles.courseCardMeta}>
                  {[selectedCourse.clubName, selectedCourse.city, selectedCourse.country].filter(Boolean).join(' · ')}
                </Text>
                <Text style={styles.courseCardMeta}>
                  {t('golfCourseAdminPage.courseCardDetails', {
                    par: String(selectedCourse.par),
                    holes: String(selectedCourse.holesCount),
                    visibility: selectedCourse.visibility,
                  })}
                </Text>

                <Text style={styles.detailSectionTitle}>{t('golfCourseAdminPage.osmSectionTitle')}</Text>
                {!selectedCourse.osmType || !selectedCourse.osmId ? (
                  <Text style={styles.courseCardMeta}>{t('golfCourseAdminPage.osmNotImported')}</Text>
                ) : (
                  <>
                    <Text style={styles.courseCardOsm}>
                      {t('golfCourseAdminPage.courseCardOsm', {
                        osmType: selectedCourse.osmType,
                        osmId: String(selectedCourse.osmId),
                      })}
                    </Text>
                    {osmCounts.status === 'loading' ? (
                      <ActivityIndicator size="small" color={appColors.text.primary} />
                    ) : osmCounts.status === 'error' ? (
                      <Text style={styles.errorText}>{t('golfCourseAdminPage.osmCountsError')}</Text>
                    ) : osmCounts.status === 'loaded' ? (
                      <Text testID="golf-course-admin-osm-counts" style={styles.courseCardMeta}>
                        {t('golfCourseAdminPage.osmCounts', {
                          holes: String(osmCounts.counts.holes),
                          greens: String(osmCounts.counts.greens),
                          tees: String(osmCounts.counts.tees),
                          terrain: String(osmCounts.counts.terrain),
                        })}
                      </Text>
                    ) : null}
                  </>
                )}

                {/* The import itself lives on config-course, which already carries the whole
                    flow — search, match picker, per-category counts. Sending the admin there
                    with the course loaded beats a second copy of it here: the write path that
                    matters (features/import, which sets osm_type/osm_id and lays down the tee,
                    green and centreline rows) is the same one either way. */}
                <Pressable
                  testID="golf-course-admin-osm-import"
                  accessibilityRole="button"
                  style={styles.scanButton}
                  onPress={() => {
                    const courseId = selectedCourse.id;
                    setSelectedCourse(null);
                    router.push(`/config-course?manage=1&courseId=${encodeURIComponent(courseId)}`);
                  }}
                >
                  <Ionicons name="map" size={18} color={appColors.text.primary} />
                  <Text style={styles.scanButtonText}>{t('courseConfig.osmButton')}</Text>
                </Pressable>

                <Pressable
                  testID="course-hole-map-open"
                  style={styles.scanButton}
                  onPress={() => setHoleMapOpen(true)}
                >
                  <Ionicons name="location" size={18} color={appColors.text.primary} />
                  <Text style={styles.scanButtonText}>{t('courseHoleMap.openButton')}</Text>
                </Pressable>

                <Text style={styles.detailSectionTitle}>{t('golfCourseAdminPage.analysisSectionTitle')}</Text>
                <CourseAnalysisStatusPanel apiBaseUrl={apiBaseUrl} token={user?.token} courseId={selectedCourse.id} />
              </ScrollView>
            ) : null}
          </Pressable>
        </Pressable>
      </Modal>

      {/* Marking positions is offered on the web only. It is precision mouse work on
          satellite imagery — pointing at a tee box a few metres across — and the phone this
          app usually runs on is the wrong tool for it, not merely a smaller one. */}
      <CourseHoleMapModal
        visible={holeMapOpen && selectedCourse !== null}
        onClose={() => setHoleMapOpen(false)}
        apiBaseUrl={apiBaseUrl}
        token={user?.token}
        courseId={selectedCourse?.id ?? null}
        holeNumbers={
          selectedCourse ? Array.from({ length: selectedCourse.holesCount }, (_, index) => index + 1) : []
        }
        editable={isAdmin && Platform.OS === 'web'}
      />
    </>
  );
}

/**
 * The batch-worker path of the flyover modal: whether a worker is switched on, how the last
 * render went, and the button that queues a new one. Rendering happens on a machine outside
 * the cloud that may be off for days, so "queued" with no worker online is a normal state the
 * admin needs to be told about, not an error.
 */
function FlyoverJobStatusBlock({
  status,
  request,
  onRender,
}: {
  status: FlyoverStatus | null | undefined;
  request: { state: FlyoverRequestState; code?: string } | undefined;
  onRender: () => void;
}) {
  const { t } = useI18n();
  const requesting = request?.state === 'requesting';
  const job = status?.latestJob ?? null;
  // Nothing to fly = the render endpoint would refuse (409 no_holes), so don't offer it.
  // Unknown (older geodata, or status not loaded yet) leaves the button enabled.
  const nothingToFly = status?.flyableHoles === 0;
  return (
    <View style={styles.flyoverJobBlock}>
      {status ? (
        <Text
          testID="golf-course-admin-flyover-worker"
          style={status.workerOnline ? styles.statusText : styles.errorText}
        >
          {status.workerOnline
            ? t('golfCourseAdminPage.flyoverWorkerOnline')
            : t('golfCourseAdminPage.flyoverWorkerOffline')}
        </Text>
      ) : null}
      {nothingToFly ? (
        <Text testID="golf-course-admin-flyover-nothing-to-fly" style={styles.errorText}>
          {t('golfCourseAdminPage.flyoverNothingToFly')}
        </Text>
      ) : null}
      {job ? (
        <Text testID="golf-course-admin-flyover-last-job" style={styles.progressText}>
          {t('golfCourseAdminPage.flyoverLastJob', {
            status: t(`golfCourseAdminPage.flyoverJobStatus.${job.status}`),
            rendered: job.renderedHoles ?? 0,
            skipped: job.skippedHoles ?? 0,
          })}
          {job.workerId ? ` · ${job.workerId}` : ''}
          {job.error ? ` · ${job.error}` : ''}
        </Text>
      ) : null}
      <View style={styles.flyoverCopyRow}>
        <Pressable
          testID="golf-course-admin-flyover-render"
          accessibilityRole="button"
          disabled={requesting || nothingToFly}
          aria-disabled={requesting || nothingToFly}
          style={[styles.analyzeButton, (requesting || nothingToFly) && styles.actionButtonDisabled]}
          onPress={onRender}
        >
          <Text style={styles.analyzeButtonText}>
            {requesting
              ? t('golfCourseAdminPage.flyoverRequesting')
              : t('golfCourseAdminPage.flyoverRender')}
          </Text>
        </Pressable>
        {request?.state === 'queued' ? (
          <Text testID="golf-course-admin-flyover-queued" style={styles.statusText}>
            {t('golfCourseAdminPage.flyoverQueued')}
          </Text>
        ) : request?.state === 'failed' ? (
          <Text testID="golf-course-admin-flyover-error" style={styles.errorText}>
            {t(flyoverFailureKey(request.code))}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
  },
  paragraph: {
    fontSize: 14,
    lineHeight: 20,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  searchInput: {
    flex: 1,
    color: appColors.text.primary,
    fontSize: 14,
  },
  searchResults: {
    gap: 8,
  },
  filterRow: {
    flexDirection: 'row',
    gap: 8,
  },
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  filterChipActive: {
    borderColor: '#A1D493',
    backgroundColor: '#1F5B2E',
  },
  filterChipText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '600',
  },
  filterChipTextActive: {
    color: appColors.text.primary,
  },
  listCountryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  osmFilterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  smallCheckbox: {
    width: 16,
    height: 16,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#A1D493',
    alignItems: 'center',
    justifyContent: 'center',
  },
  smallCheckboxMark: {
    color: appColors.text.inverse,
    fontSize: 11,
    fontWeight: '900',
    lineHeight: 13,
  },
  selectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
  },
  selectAllRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pagerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  pagerButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
  },
  courseCard: {
    borderWidth: 1,
    borderColor: '#1A3A20',
    borderRadius: 10,
    padding: 12,
    gap: 2,
  },
  courseCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  courseCardName: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  courseCardMeta: {
    color: appColors.text.secondary,
    fontSize: 12,
  },
  coordinateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  inlineLink: {
    color: appColors.accent.primary,
    fontSize: 12,
    textDecorationLine: 'underline',
  },
  courseCardOsm: {
    color: appColors.text.secondary,
    fontSize: 11,
    marginTop: 2,
  },
  flyoverStateRendered: {
    color: appColors.status.success,
    fontSize: 11,
    marginTop: 2,
  },
  analysisRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  visibilityChipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  visibilityChip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  visibilityChipActive: {
    borderColor: '#A1D493',
    backgroundColor: '#1F5B2E',
  },
  visibilityChipText: {
    color: '#C8C8B0',
    fontSize: 11,
    fontWeight: '700',
  },
  visibilityChipTextActive: {
    color: appColors.text.primary,
  },
  analysisBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  analysisBadgeGood: { backgroundColor: '#1F5B2E' },
  analysisBadgeWarn: { backgroundColor: '#6B5A1E' },
  analysisBadgeBad: { backgroundColor: '#7A2E2E' },
  analysisBadgeText: {
    color: appColors.text.primary,
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  analyzeButton: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
  },
  flyoverJobBlock: {
    marginTop: 8,
    marginBottom: 12,
    gap: 6,
  },
  linkText: {
    color: appColors.accent.primary,
    textDecorationLine: 'underline',
    marginTop: 4,
  },
  flyoverCommandBox: {
    marginTop: 10,
    borderRadius: 8,
    backgroundColor: '#03170A',
    padding: 12,
    maxHeight: 260,
  },
  flyoverCommandText: {
    color: '#D9F2DC',
    fontSize: 12,
    fontFamily: Platform.select({ web: 'monospace', ios: 'Menlo', default: 'monospace' }),
  },
  flyoverCopyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 12,
  },
  analyzeButtonText: {
    color: appColors.accent.primary,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  analyzeQueuedText: {
    color: appColors.accent.primary,
    fontSize: 11,
    marginTop: 2,
  },
  detailSectionTitle: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
    marginTop: 12,
    marginBottom: 6,
  },
  scanButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: 8,
    paddingVertical: 11,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#0C2510',
  },
  scanButtonText: {
    color: appColors.text.primary,
    fontSize: 15,
    fontWeight: '700',
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  pickerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.35)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  osmModalOverlay: {
    backgroundColor: 'rgba(0,0,0,0.6)',
  },
  osmModalCard: {
    width: '92%',
    maxWidth: 520,
    maxHeight: '85%',
    alignSelf: 'center',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.elevated,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.45,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 12 },
    elevation: 12,
  },
  osmModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
  },
  osmModalClose: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
  },
  osmModalBody: {
    flexShrink: 1,
    padding: 16,
    gap: 10,
  },
  osmPickerTitle: {
    flex: 1,
    color: appColors.text.primary,
    fontSize: 16,
    fontWeight: '700',
  },
  countryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 4,
  },
  countryList: {
    maxHeight: 320,
  },
  officialRow: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 12,
    paddingVertical: 4,
  },
  officialLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#07200D',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#A1D493',
  },
  checkboxMark: {
    color: appColors.text.inverse,
    fontSize: 15,
    fontWeight: '900',
    lineHeight: 17,
  },
  pickerOption: {
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderColor: 'rgba(51,69,109,0.6)',
  },
  pickerOptionText: {
    color: appColors.text.primary,
    fontSize: 15,
  },
  statusText: {
    color: appColors.text.primary,
  },
  progressText: {
    color: appColors.text.secondary,
    fontSize: 13,
  },
  errorText: {
    color: '#b91c1c',
  },
  importedListTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: appColors.text.primary,
    marginTop: 6,
  },
  importedList: {
    maxHeight: 240,
  },
  importedListItem: {
    color: appColors.text.primary,
    fontSize: 13,
    paddingVertical: 4,
  },
});
